/**
 * フォーマット関連のユーティリティ関数
 */

import { AiParam } from "graphql/types";
import { Scenario } from "../types/models";

/**
 * 評価レポートをMarkdown形式で生成する
 * @param scenario シナリオ情報
 * @param evaluation 評価情報（セッション、評価結果、会話履歴を含む）
 * @returns Markdown形式のレポート文字列
 */
export const generateMarkdownReport = (
  scenario: any,
  evaluation: {
    session?: any;
    evaluationText?: string;
    conversations?: any[];
    date?: string;
    duration?: string;
  },
  displayName?: string|undefined
): string => {
  if (!scenario || !evaluation) {
    return '';
  }

  const { session, evaluationText, conversations = [] } = evaluation;

  // 実施概要のセクション
  let markdown = `# 評価レポート: ${scenario.title}\n\n`;
  markdown += `## 実施概要\n\n`;
  markdown += `- **実施者**: ${ displayName || session?.createdBy || '不明'}\n`;
  
  // 開始日時と終了日時をフォーマット
  const startDate = session?.startTime ? new Date(session.startTime).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) : '不明';
  
  const endDate = session?.endTime ? new Date(session.endTime).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) : '不明';
  
  markdown += `- **開始日時**: ${startDate}\n`;
  markdown += `- **終了日時**: ${endDate}\n\n`;
  
  // シナリオ情報のセクション
  markdown += `## シナリオ情報\n\n`;
  markdown += `- **シナリオ名**: ${scenario.title}\n`;
  markdown += `- **目安時間**: ${scenario.estimatedTime || '指定なし'}分\n`;
  markdown += `- **概要**: ${scenario.description || '記載なし'}\n`;
  markdown += `- **目的**: ${scenario.goal || '記載なし'}\n`;
  markdown += `- **状況設定**: ${scenario.situation || '記載なし'}\n`;
  markdown += `- **相手の名前**: ${scenario.targetName || '記載なし'}\n`;
  markdown += `- **性別**: ${scenario.targetGender || '記載なし'}\n`;
  markdown += `- **年代**: ${scenario.targetAge || '記載なし'}\n`;
  
  // 資料情報
  if (scenario.materials && scenario.materials.length > 0) {
    markdown += `- **資料**:\n`;
    scenario.materials.forEach((material: any) => {
      markdown += `  - ${material.name}\n`;
    });
  } else {
    markdown += `- **資料**: なし\n`;
  }

  // セッション設定
  markdown += `\n## セッション設定\n\n`;
  markdown += `- **キャラクター設定**: ${session?.characterSettings || '記載なし'}\n`;
  markdown += `- **ガイドの有無**: ${session?.aiGuide ? 'あり' : 'なし'}\n`;
  markdown += `- **会話字幕の有無**: ${session?.aiSubtitleDisplay ? 'あり' : 'なし'}\n`;

  // 評価結果
  markdown += `\n## 評価結果\n\n`;
  markdown += evaluationText ? evaluationText : '評価結果はまだ生成されていません。';

  // 会話履歴
  markdown += `\n\n## 会話履歴\n\n`;
  
  if (conversations.length > 0) {
    // 会話を時系列順に並べる（counterフィールドを使用）
    const sortedConversations = [...conversations].sort((a, b) => 
      (a.counter || 0) - (b.counter || 0)
    );
    
    sortedConversations.forEach((conversation: any) => {
      // 担当者（ユーザー）のメッセージ
      if (conversation.userContent) {
        markdown += `### 担当者:\n`;
        markdown += `${conversation.userContent}\n\n`;
      }
      
      // お客様（AI）のメッセージ
      if (conversation.aiContent) {
        markdown += `### お客様:\n`;
        // 評価情報
        markdown += `*評価: ${formatAiParams(conversation.aiParams)}*\n`;
        markdown += `${conversation.aiContent}\n\n`;
      }
    });
  } else {
    markdown += '会話履歴はありません。';
  }

  return markdown;
};

/**
 * aiParamsの文字列を解析してオブジェクトに変換する
 * @param aiParamsString AIパラメータの文字列
 * @returns パース済みのAIパラメータオブジェクト
 */
export const parseAiParams = (aiParamsString: string | undefined): any => {
  if (!aiParamsString) return null;
  
  try {
    // 文字列からスコアと理由を抽出する正規表現
    const levelMatch = aiParamsString.match(/level=\{score=([^,]+), reason=([^}]+)\}/);
    
    if (levelMatch && levelMatch.length >= 3) {
      return {
        level: {
          score: parseInt(levelMatch[1], 10),
          reason: levelMatch[2].trim()
        }
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing aiParams string:', error);
    return null;
  }
};

/**
 * aiParamsをフォーマットする
 * @param aiParams AIパラメータオブジェクト
 * @returns フォーマットされたAIパラメータ文字列
 */
export const formatAiParams = (aiParams: AiParam | string | undefined): string => {
  if (!aiParams) return '';

  // 文字列の場合はパースを試みる
  if (typeof aiParams === 'string') {
    const parsedParams = parseAiParams(aiParams);
    if (parsedParams) {
      return `レベル ${parsedParams.level.score} (${parsedParams.level.reason})`;
    }
    return '';
  }
  
  // オブジェクトの場合
  try {
    return `レベル ${aiParams.level.score} (${aiParams.level.reason.trim()})`;
  } catch (error) {
    console.error('Error formatting aiParams object:', error);
    return '';
  }
};

/**
 * 会話の日時をフォーマットする
 * @param timestamp タイムスタンプ文字列
 * @returns フォーマットされた時刻文字列
 */
export const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch (error) {
    return timestamp;
  }
};

/**
 * セッション時間を計算する
 * @param startTime 開始時間
 * @param endTime 終了時間
 * @returns フォーマットされたセッション時間
 */
export const calculateSessionDuration = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return '';
  
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    
    const minutes = Math.floor(durationMs / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } catch (error) {
    return '';
  }
};

/**
 * 空文字、null、undefinedを等しく扱うヘルパー関数
 * @param value チェックする値
 * @returns ブランクかどうか
 */
export const isBlank = (value: string | null | undefined): boolean => {
  return value === "" || value === null || value === undefined;
};

/**
 * ユーザーの部署と一致するシナリオのみをフィルタリングする関数
 * @param scenarios シナリオ一覧
 * @param userDepartment ユーザーの部署
 * @returns フィルタリング済みのシナリオ一覧
 */
export const filterByDepartment = (scenarios: Scenario[], userDepartment: string | null | undefined, isAdmin: boolean = false): Scenario[] => {
  return scenarios.filter((scenario: Scenario) => {
    const scenarioDepartment = scenario.department;

    // 管理者機能の場合
    if (isAdmin) {
      // ユーザーの部署が設定されていない、またはシナリオの部署がユーザーの部署と一致するシナリオを表示
      return isBlank(userDepartment) || userDepartment === scenarioDepartment;
    } else {
      // 管理者機能以外の場合、部署が設定されていないシナリオ、または部署がユーザーの部署と一致するシナリオを表示
      return isBlank(scenarioDepartment) || userDepartment === scenarioDepartment;
    }
  });
};
